import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, X } from 'lucide-react';

const ImportPreview = ({ previewData, validation, totalRows }) => {
  const { t } = useTranslation();

  const getRowStatus = (rowNumber) => {
    const rowErrors = validation?.rowErrors?.[rowNumber] || [];
    if (rowErrors.length > 0) {
      return { status: 'error', errors: rowErrors };
    }
    return { status: 'success', errors: [] };
  };

  if (!previewData || previewData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('excelImport.noPreviewData', 'No preview data available')}
      </div>
    );
  }

  // Get all unique keys from preview data
  const allKeys = new Set();
  previewData.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!key.startsWith('_')) {
        allKeys.add(key);
      }
    });
  });

  const columns = Array.from(allKeys);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('excelImport.dataPreview', 'Data Preview')}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('excelImport.showingRows', `Showing ${previewData.length} of ${totalRows} rows`)}
        </span>
      </div>

      {validation && !validation.valid && validation.errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                {t('excelImport.validationErrors', 'Validation Errors')}
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                {validation.errors.slice(0, 10).map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
                {validation.errors.length > 10 && (
                  <li className="text-red-600 dark:text-red-400">
                    {t('excelImport.andMore', `... and ${validation.errors.length - 10} more errors`)}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {validation && validation.warnings && validation.warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                {t('excelImport.validationWarnings', 'Validation Warnings')}
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                {validation.warnings.slice(0, 10).map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
                {validation.warnings.length > 10 && (
                  <li className="text-yellow-600 dark:text-yellow-400">
                    {t('excelImport.andMoreWarnings', `... and ${validation.warnings.length - 10} more warnings`)}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('excelImport.row', 'Row')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('excelImport.status', 'Status')}
              </th>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {previewData.map((row, idx) => {
              const rowNumber = row._rowNumber || idx + 1;
              const rowStatus = getRowStatus(rowNumber);

              return (
                <tr
                  key={idx}
                  className={rowStatus.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' : ''}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {rowNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {rowStatus.status === 'error' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <X className="h-3 w-3 mr-1" />
                        {t('excelImport.error', 'Error')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {t('excelImport.ok', 'OK')}
                      </span>
                    )}
                  </td>
                  {columns.map((column) => (
                    <td
                      key={column}
                      className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate"
                      title={row[column] || ''}
                    >
                      {row[column] !== null && row[column] !== undefined ? String(row[column]) : '-'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {validation && validation.rowErrors && Object.keys(validation.rowErrors).length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            {t('excelImport.rowErrors', 'Row Errors')}
          </h4>
          {Object.entries(validation.rowErrors).slice(0, 5).map(([rowNumber, errors]) => (
            <div
              key={rowNumber}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
            >
              <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                {t('excelImport.row', 'Row')} {rowNumber}:
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300">
                {errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportPreview;

